import { LazyIssueApp } from '@jira/issue__lazy-app/src/LazyIssueApp.tsx';
import { currentUserResource } from '@jira/router-resources__current-user/src/useCurrentUserResource.tsx';
import { MenuIdProjects } from '@jira/navigation-apps__constants/src/MenuIdProjects.tsx';
import { SidebarIdContent } from '@jira/navigation-apps__constants/src/SidebarIdContent.tsx';
import { aiEntitlementResource } from '@jira/router-resources__ai-entitlement/src/aiEntitlementResource.tsx';
import { TEAM_MANAGED_BACKLOG } from '@jira/platform__observability-capabilities/src/constants.tsx';
import { fg } from '@atlassian/jira-feature-gating';
import { AsyncHorizontalNavJira } from '@jira/navigation-apps__horizontal-nav-jira/src/async.tsx';
import { embeddedIssuePreloadLayoutBuilder } from '@jira/platform__layouts-issue-preload-layout/src/index.tsx';
import { getIssueViewResources } from '@jira/router-resources__issue-view/src/getIssueViewResources.tsx';
import {
	forgeBacklogModuleResource,
	forgeSprintModuleResource,
} from '@jira/navigation-apps__resources/src/controllers/forge/index.tsx';
import { createEntry } from '@jira/platform__routing-route-entry/src/createEntry.tsx';
import { composeLayouts } from '@jira/platform__layouts-route-layout/src/GlobalComponent.tsx';
import { getNavigationResources } from '@jira/router-resources__navigation/src/getNavigationResources.tsx';
import { backlogResource } from '@jira/router-resources__next-gen-backlog/src/backlogResource.tsx';
import { epicsLiteResource } from '@jira/router-resources__next-gen-backlog/src/epicsLiteResource.tsx';
import { permissionsResource } from '@jira/router-resources__next-gen-backlog/src/permissionsResource.tsx';
import { backlogFilterRefinementResource } from '@jira/router-resources__next-gen-backlog/src/backlogFilterRefinementResource.tsx';
import { confluenceUserPermissionsResource } from '@jira/router-resources__confluence-user-permissions/src/confluenceUserPermissionsResource.tsx';
import { projectContextResource } from '@jira/router-resources__project-context/src/projectContextResource.tsx';
import { softwareBacklogRoute } from '@jira/router-routes__software-backlog-routes/src/softwareBacklogRoute.tsx';

import { NextGenBacklogSkeleton } from '@jira/platform__skeletons/src/ui/next-gen-backlog/NextGenBacklogSkeleton.tsx';
import { softwareProjectLayout } from '@jira/platform__layouts-software-project-layout/src/index.tsx';
import { resourceWithCondition } from '@jira/router-resources__utils/src/resourceWithCondition.tsx';
import { projectThemeResource } from '@jira/router-resources__project-theme/src/projectThemeResource.tsx';
import { backlogPageEntrypoint, GSNBacklogPageEntrypoint } from './ui/index.tsx';
import type { Route } from '@atlassian/react-resource-router';

export const softwareBacklogRouteEntry: Route = createEntry(softwareBacklogRoute, () => ({
	group: 'software',
	skeleton: NextGenBacklogSkeleton,
	layout: composeLayouts(
		softwareProjectLayout,
		embeddedIssuePreloadLayoutBuilder([
			{
				query: ['selectedIssue'],
			},
		]),
	),
	navigation: {
		menuId: MenuIdProjects,
		horizontal: AsyncHorizontalNavJira,
		sidebarId: SidebarIdContent,
	},
	resources: [
		...getNavigationResources({ isUsingSidebarEntrypoint: true }),
		...getIssueViewResources(),
		// eslint-disable-next-line jira/router/no-project-context-resource
		projectContextResource,
		backlogResource,
		resourceWithCondition(() => !__SERVER__ || fg('enable_ssr_for_cmp_backlog'), epicsLiteResource),
		backlogFilterRefinementResource,
		forgeBacklogModuleResource,
		forgeSprintModuleResource,
		projectThemeResource,
		aiEntitlementResource,
		currentUserResource,
		permissionsResource,
		resourceWithCondition(
			() => fg('twcg-992-loom-meeting-notes-sprint-planning-gate'),
			confluenceUserPermissionsResource,
		),
	],
	preloadOptions: {
		earlyChunks: ['software-backlog-early-entry'],
	},
	afterPaint: [LazyIssueApp],
	ufoName: 'next-gen-backlog',
	meta: {
		capability: TEAM_MANAGED_BACKLOG,
	},
	entryPoint: () => (fg('navx-global-side-nav') ? GSNBacklogPageEntrypoint : backlogPageEntrypoint),
	supportsPreviewPanel: true,
	supportsProjectTheme: 'full',
	isHydrateRootEnabled: true,
	isStreamingEnabled: fg('jfp-magma-ssr-streaming-enable-backlog'),
	bifrost: {
		bifrostGroup: 'shell-only',
	},
}));
